/**
 * feat-241 — the station-readiness matrix page.
 *
 * Answers the question no screen answers today: "who can I put on Barista
 * tonight?" Crew down the side, active curriculum tracks across the top, one
 * state cell per pair (done / N of M / open / locked).
 *
 * A SIBLING of Training Progress, not a widening of it (P1). That page is a
 * rolling-14-day ADHERENCE grid with a goal basis; coverage is a different
 * question, and `TrainingProgressPage.tsx` is byte-identical after this feature
 * (AC21).
 *
 * Ordering lives in `coverageSort.ts` and the cells in `CoverageStateCell.tsx`;
 * this page holds the sort SELECTION and the empty/error/loading states.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ScorecardNavigation from './ScorecardNavigation';
import {
  COVERAGE_EMPTY_BODY,
  COVERAGE_EMPTY_TITLE,
  COVERAGE_ERROR_BODY,
  COVERAGE_ERROR_RETRY,
  COVERAGE_ERROR_TITLE,
  COVERAGE_LOADING,
  COVERAGE_PAGE_SUBTITLE,
  COVERAGE_PAGE_TITLE,
} from '../../constants/curriculumCoverage';
import { CoverageMatrix } from './curriculumCoverage/CoverageMatrix';
import { useCurriculumCoverage } from './curriculumCoverage/CurriculumCoverageProvider';
import {
  DEFAULT_SORT,
  sortCrew,
  type SortDirection,
  type SortKey,
} from './curriculumCoverage/coverageSort';

const CurriculumCoveragePage: React.FC = () => {
  const navigate = useNavigate();
  const {
    data,
    loading,
    error,
    forbidden,
    selectedLocationId,
    setSelectedLocationId,
    availableLocations,
    canFilterByLocation,
    retry,
  } = useCurriculumCoverage();

  const [sortKey, setSortKey] = useState<SortKey>(DEFAULT_SORT.key);
  const [sortDirection, setSortDirection] = useState<SortDirection>(DEFAULT_SORT.direction);

  /**
   * Clicking the ACTIVE column flips direction; clicking a new column adopts
   * that column's most useful default. For a track column that is `desc` —
   * most-ready first, which is what "who can I put on Barista tonight" asks. For
   * the name and location columns it is A–Z.
   */
  const handleSort = (key: SortKey) => {
    const same =
      key.kind === sortKey.kind &&
      (key.kind !== 'track' || (sortKey.kind === 'track' && key.trackId === sortKey.trackId));
    if (same) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDirection(key.kind === 'name' || key.kind === 'location' ? 'asc' : 'desc');
  };

  const sortedCrew = useMemo(() => {
    if (!data) return [];
    return sortCrew(data.crew, data.tracks, sortKey, sortDirection);
  }, [data, sortKey, sortDirection]);

  return (
    // The site chrome. THIS CODEBASE HAS NO SHARED LAYOUT WRAPPER — every page
    // renders <ScorecardNavigation /> itself (TrainingProgressPage:348,
    // LiveServers and PilotDashboardPage all do), and these two pages never did,
    // which dropped the user out of the site's navigation entirely — tenant
    // selector included, and on a multi-tenant surface that is not optional.
    // ScorecardNavigation renders TenantSelector itself, so this one component
    // delivers the whole framework.
    //
    // The wrapper/main split is copied from TrainingProgressPage VERBATIM rather
    // than adapted: a page that is NEARLY the same shape as its neighbours is
    // worse than one that is either identical or deliberately different. The
    // padding this page used to carry on its outer div now lives on the <main>,
    // which is where every sibling puts it.
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />

      <main className="mx-auto max-w-7xl px-4 md:px-8 py-8">
        <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl text-ink">{COVERAGE_PAGE_TITLE}</h1>
            <p className="mt-1 text-sm text-inkMuted">{COVERAGE_PAGE_SUBTITLE}</p>
          </div>
          {canFilterByLocation ? (
            <select
              value={selectedLocationId ?? ''}
              onChange={(e) => setSelectedLocationId(e.target.value || null)}
              aria-label="Filter by location"
              className="rounded-full border border-softBorder bg-white px-3.5 py-2 font-sans text-sm font-semibold text-ink outline-none focus:border-coral focus:ring-2 focus:ring-coral/30"
            >
              <option value="">All locations</option>
              {availableLocations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          ) : null}
        </header>

        {/* design F4 (a11y half) — the designed skeleton is out of scope, but the
            loading state must at least be ANNOUNCED. `aria-busy` marks the region
            as in flux and the polite live region gives a screen reader something
            to say; previously this was a bare <p> that might not be announced at
            all. */}
        <div aria-busy={loading} aria-live="polite">
          {loading ? (
            <>
              <span className="sr-only">{COVERAGE_LOADING}</span>
              <p className="text-sm text-inkMuted" aria-hidden>
                {COVERAGE_LOADING}
              </p>
            </>
          ) : null}
        </div>

        {/* design F5 — the error state needs a sentence and a way out, not just a
            title. REACHABLE ON PURPOSE during the rollout window: this PR deploys
            after the backend's, and the spec's sequencing says this surface
            "degrades to an error state without it". The retry re-runs the
            provider's single fetch rather than issuing its own (P12). */}
        {!loading && (error || forbidden) ? (
          <div
            role="alert"
            className="rounded-2xl border border-softBorder bg-white px-6 py-12 text-center"
          >
            <h2 className="font-display text-lg font-semibold text-ink">{COVERAGE_ERROR_TITLE}</h2>
            <p className="mx-auto mt-1.5 max-w-[46ch] text-sm text-inkMuted">{COVERAGE_ERROR_BODY}</p>
            <button
              type="button"
              onClick={retry}
              className="mt-5 rounded-lg bg-coral px-4 py-2 text-sm font-bold text-white focus:outline-none focus:ring-2 focus:ring-coral/40"
            >
              {COVERAGE_ERROR_RETRY}
            </button>
          </div>
        ) : null}

        {/* AC20: a store manager scoped to no location, and a tenant with no crew,
            land on the SAME empty state. Neither is an error. A tenant with only an
            implicit default track gets a one-column matrix, and a tenant whose only
            track has zero active lessons gets one column of `open` — both truthful,
            and both fall out of rendering the response as given rather than
            forking on has-tracks/no-tracks. */}
        {!loading && !error && !forbidden && data && data.crew.length === 0 ? (
          <div className="rounded-2xl border border-softBorder bg-white p-6">
            <h2 className="text-sm font-bold text-ink">{COVERAGE_EMPTY_TITLE}</h2>
            <p className="mt-1 text-sm text-inkMuted">{COVERAGE_EMPTY_BODY}</p>
          </div>
        ) : null}

        {!loading && !error && !forbidden && data && data.crew.length > 0 ? (
          <CoverageMatrix
            tracks={data.tracks}
            crew={sortedCrew}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSort={handleSort}
            onOpenPassport={(serverId) => navigate(`/restaurant/curriculum-coverage/${serverId}`)}
          />
        ) : null}
      </main>
    </div>
  );
};

export default CurriculumCoveragePage;
